import * as XLSX from '@e965/xlsx';
import { collectQuestionColumns, formatAnswerValue } from './responseFormat';

function sanitizeFilename(title) {
  const base = (title || 'survey-responses')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .slice(0, 80);
  return base || 'survey-responses';
}

/**
 * @param {{ survey_title?: string, total_count?: number, truncated?: boolean, responses: Array }} data
 * @returns {boolean} false when there is nothing to export
 */
export function exportSurveyResponsesToExcel(data) {
  const responses = data?.responses ?? [];
  if (responses.length === 0) {
    return false;
  }

  const { keys: questionKeys, labels } = collectQuestionColumns(responses);
  const hasLocation = responses.some((r) => r.location);
  const locationHeaders = hasLocation
    ? ['Client', 'Country', 'City', 'Building', 'Floor', 'Facility']
    : [];
  const headers = [
    'Response ID',
    'Submitted (UTC)',
    'Survey Version',
    ...locationHeaders,
    ...questionKeys.map((k) => labels.get(k) || k),
  ];

  const rows = responses.map((r) => {
    const byKey = Object.fromEntries(
      (r.answers ?? []).map((a) => [a.question_key, formatAnswerValue(a)]),
    );
    const loc = r.location ?? {};
    const locationCells = hasLocation
      ? [loc.client ?? '', loc.country ?? '', loc.city ?? '', loc.building ?? '', loc.floor ?? '', loc.facility ?? '']
      : [];
    return [
      r.id,
      r.submitted_at_utc ? new Date(r.submitted_at_utc).toISOString() : '',
      r.survey_version_id ?? '',
      ...locationCells,
      ...questionKeys.map((k) => byKey[k] ?? ''),
    ];
  });

  const sheetRows = [headers, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(sheetRows);

  const colWidths = headers.map((h, colIdx) => {
    const maxLen = Math.max(
      String(h).length,
      ...rows.map((row) => String(row[colIdx] ?? '').length),
    );
    return { wch: Math.min(Math.max(maxLen + 2, 12), 48) };
  });
  ws['!cols'] = colWidths;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Responses');

  if (data.truncated && data.total_count > responses.length) {
    const info = [
      ['Survey', data.survey_title ?? ''],
      ['Total responses', data.total_count],
      ['Exported rows', responses.length],
      [
        'Note',
        `This file includes the ${responses.length} most recent submission(s). Re-export after increasing the API limit if you need older rows.`,
      ],
    ];
    const infoWs = XLSX.utils.aoa_to_sheet(info);
    XLSX.utils.book_append_sheet(wb, infoWs, 'Export info');
  }

  const dateStamp = new Date().toISOString().slice(0, 10);
  const filename = `${sanitizeFilename(data.survey_title)}_responses_${dateStamp}.xlsx`;
  XLSX.writeFile(wb, filename);
  return true;
}
